import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import deliveryChoiceState from './DeliveryChoice.state';
import scrollConstants from '@constants/scrollConstants';
import deliveryConstants from '@constants/deliveryConstants';
import { DeliveryChoiceTabProps } from '@components/delivery/delivery-choice-tabs/DeliveryChoiceTabs.types';
import { recordCustomerInExperiment } from '@redux/actions/app/appActions';

// Components
import Container from '@components/container';
import DeliveryChoiceTabs from '@components/delivery/delivery-choice-tabs';
import DeliveryChoiceRadioButtons from '@components/delivery/delivery-choice-radio-buttons';
import Heading from '@components/heading';
import ScrollAnchor from '@components/scroll-anchor';
import ThresholdBar from '@components/threshold-bar';

// Utils
import isClickAndCollectAvailable from '@utils/collection/isClickAndCollectAvailable';
import { isEmptyObject } from '@utils/object';

const DeliveryChoice = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    deliveryOptions = {},
    showClickCollectNowAvailableMessage,
    collectionNotAvailableMessage,
    showAmendQuantityNotification,
    submitOrderFailed,
    titleText,
    isExpressPayment,
    thresholdBarEnabled,
    tabsDisabled,
    deliveryCollectionRadioButtonsFeature,
    shipFromStoreItemsExist,
  } = useSelector(deliveryChoiceState);

  const clickAndCollectAvailable = isClickAndCollectAvailable(deliveryOptions);

  const tabs = [{
    'data-testid': 'delivery-choice-delivery-button',
    deliveryId: deliveryConstants.DELIVERY,
    disabled: tabsDisabled,
    isAvailable: true,
    label: 'Delivery',
  }] as DeliveryChoiceTabProps[];

  if (clickAndCollectAvailable) {
    const collectionTile = {
      'data-testid': 'delivery-choice-click-collect-button',
      deliveryId: deliveryConstants.CLICK_AND_COLLECT,
      disabled: tabsDisabled,
      isAvailable: clickAndCollectAvailable,
      label: 'Collection',
      showClickCollectNowAvailableMessage,
    };

    tabs.unshift(collectionTile);
  }

  useEffect(() => {
    if (tabs.length === 2 && deliveryCollectionRadioButtonsFeature?.impressionId) {
      dispatch(recordCustomerInExperiment(deliveryCollectionRadioButtonsFeature?.impressionId));
    }
  }, []);

  const collectionIsNotAvailable =
    !isEmptyObject(deliveryOptions) &&
    !isClickAndCollectAvailable(deliveryOptions) &&
    !showClickCollectNowAvailableMessage;
  let msgTitle;
  let msgBody = 'Please review your order as your delivery options and/or prices may have changed.';

  if (showAmendQuantityNotification) {
    if (submitOrderFailed) {
      msgTitle = 'Your order has not been placed';
      msgBody = 'Sorry, we could not complete your order. This may be because your basket or selected delivery options have changed. Please review your order and try again.';
    } else if (showClickCollectNowAvailableMessage) {
      msgTitle = 'Your basket has been updated and collection is now available';
    } else {
      msgTitle = 'Your basket has been updated';
    }
  }

  let shouldShowAmendQuantityNotificationShow = showAmendQuantityNotification;
  if (submitOrderFailed && isExpressPayment) {
    shouldShowAmendQuantityNotificationShow = false;
  }

  const showCollectionUnavailableMessage = collectionIsNotAvailable;
  const showShipFromStoreMessage = shipFromStoreItemsExist;
  const showShipFromStoreMessageBody = (
    <ul className="mb-0">
      <li className="mb-0">Collection is not available for items delivered from store.</li>
      <li className="mb-0">You can only buy one item due to limited stock in store.</li>
      <li className="mb-0">Special offers and promotions may not apply.</li>
    </ul>
  );

  return (
    <Container background="white">
      <Container
        borderBottom={tabs?.length !== 2 ? 'grey-20' : ''}
        tag="header"
      >
        <Container paddingX="3" paddingY="2">
          <Heading
            label={titleText}
            tag="h2"
            testId="delivery-title"
            type="s"
          />
        </Container>
        {thresholdBarEnabled &&
          <ThresholdBar placementLocation="delivery" />}
        <Container
          marginBottom="3"
          marginX="3"
          renderIf={shouldShowAmendQuantityNotificationShow}
        >
          <Message
            body={msgBody}
            dataTestId="basket-amended-message"
            title={msgTitle}
            type="information"
          />
        </Container>

        <ScrollAnchor scrollId={scrollConstants.DELIVERY_CHOICE} />

        <Container
          flex={{
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}
          renderIf={tabs?.length === 2}
        >
          {
            deliveryCollectionRadioButtonsFeature?.enabled ?
              <DeliveryChoiceRadioButtons tabs={tabs} /> :
              <DeliveryChoiceTabs tabs={tabs} />
          }
        </Container>
      </Container>
      <Container
        marginTop="3"
        paddingX="3"
        renderIf={showCollectionUnavailableMessage}
      >
        <Message
          body={showShipFromStoreMessage ? showShipFromStoreMessageBody : null}
          dataTestId="collection-not-available"
          title={
            showShipFromStoreMessage
              ? 'Delivery from store'
              : collectionNotAvailableMessage
          }
          type="information"
        />
      </Container>
    </Container>
  );
};

export default DeliveryChoice;
